import React, { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, RotateCcw, Medal, Zap, PlayCircle } from "lucide-react";
import { academy } from "@/lib/academy";
import { apiErr } from "@/lib/api";

function Intro({ mod, onStart, busy }) {
  const q = mod.quiz;
  return (
    <div data-testid="quiz-intro">
      <h2 className="text-2xl font-bold text-zinc-50">{mod.kind === "final" ? "Final Exam" : `${mod.title} quiz`}</h2>
      <p className="mt-2 text-[14px] text-zinc-400">{mod.question_count} questions · one correct answer each · pass mark {mod.pass_pct}% · unlimited retakes. You see the explanation after every answer.</p>
      {q.best != null && <p className="mt-3 text-[14px] text-zinc-300" data-testid="quiz-best">Best score: <b>{q.best}%</b> {q.passed && <span className="ml-2 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[12px] text-emerald-300">Passed</span>}</p>}
      {q.attempts.length > 0 && (
        <div className="mt-4 overflow-hidden rounded-lg border border-zinc-800" data-testid="quiz-history">
          {q.attempts.slice().reverse().map((a, i) => (
            <div key={a.id} className="flex items-center justify-between border-b border-zinc-800/60 px-4 py-2 text-[13px] last:border-0">
              <span className="text-zinc-400">Attempt {q.attempts.length - i} · {new Date(a.at).toLocaleString()}</span>
              <span className={a.passed ? "text-emerald-400" : "text-zinc-300"}>{a.pct}% ({a.correct}/{a.total})</span>
            </div>
          ))}
        </div>
      )}
      <button data-testid="quiz-start" onClick={onStart} disabled={busy}
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-blue-600 px-5 py-2.5 text-[14px] font-semibold text-white hover:bg-blue-500 disabled:opacity-50">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <PlayCircle size={16} />} {q.attempts.length ? "Retake" : "Start"}
      </button>
    </div>
  );
}

function Question({ q, idx, total, onAnswer, feedback, onNext, busy }) {
  return (
    <div data-testid="quiz-question">
      <div className="mb-2 flex items-center justify-between text-[12px] uppercase tracking-wider text-zinc-500">
        <span>Question {idx + 1} of {total}</span>
        <div className="h-1.5 w-32 overflow-hidden rounded-full bg-zinc-800"><div className="h-full bg-blue-500" style={{ width: `${((idx + (feedback ? 1 : 0)) / total) * 100}%` }} /></div>
      </div>
      <h3 className="text-lg font-semibold leading-snug text-zinc-50" data-testid="quiz-question-text">{q.text}</h3>
      <div className="mt-5 space-y-2.5">
        {q.options.map((o, i) => {
          const chosen = feedback?.chosen === i;
          const right = feedback && feedback.correct_option === i;
          const cls = !feedback ? "border-zinc-800 hover:border-blue-500/60 hover:bg-blue-500/5" : right ? "border-emerald-500/60 bg-emerald-500/10" : chosen ? "border-red-500/60 bg-red-500/10" : "border-zinc-800 opacity-60";
          return (
            <button key={i} data-testid={`quiz-option-${i}`} disabled={!!feedback || busy} onClick={() => onAnswer(i)}
              className={`flex w-full items-start gap-3 rounded-lg border px-4 py-3 text-left text-[14px] text-zinc-200 transition-colors ${cls}`}>
              <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md border border-zinc-700 text-[12px] font-semibold text-zinc-400">{String.fromCharCode(65 + i)}</span>
              <span className="flex-1">{o}</span>
              {right && <CheckCircle2 size={18} className="shrink-0 text-emerald-400" />}
              {chosen && !right && <XCircle size={18} className="shrink-0 text-red-400" />}
            </button>
          );
        })}
      </div>
      {feedback && (
        <div className={`mt-5 rounded-lg border p-4 text-[14px] ${feedback.correct ? "border-emerald-500/30 bg-emerald-500/[0.07]" : "border-amber-500/30 bg-amber-500/[0.07]"}`} data-testid="quiz-feedback">
          <div className={`font-semibold ${feedback.correct ? "text-emerald-300" : "text-amber-300"}`}>{feedback.correct ? "Correct!" : "Not quite."}</div>
          <p className="mt-1 leading-relaxed text-zinc-300">{feedback.explanation}</p>
          <button data-testid="quiz-next" onClick={onNext} disabled={busy} className="mt-4 inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-blue-500 disabled:opacity-50">
            {busy && <Loader2 size={14} className="animate-spin" />} {idx + 1 === total ? "See result" : "Next question"}
          </button>
        </div>
      )}
    </div>
  );
}

function Result({ res, onRetry, onBack }) {
  return (
    <div className="text-center" data-testid="quiz-result">
      <div className={`mx-auto grid h-20 w-20 place-items-center rounded-full ${res.passed ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>
        {res.passed ? <Medal size={36} /> : <RotateCcw size={32} />}
      </div>
      <div className="mt-4 text-4xl font-bold text-zinc-50" data-testid="quiz-result-pct">{res.pct}%</div>
      <p className="mt-1 text-[14px] text-zinc-400">{res.correct} of {res.total} correct · pass mark {res.pass_pct}%</p>
      <p className="mt-3 text-[15px] text-zinc-200">{res.passed ? `Well done! You earned the “${res.badge}” badge.` : "Almost there. Review the lessons and try again, retakes are unlimited."}</p>
      {res.xp_gained > 0 && <p className="mt-2 inline-flex items-center gap-1 text-[14px] font-semibold text-[#FFCC00]"><Zap size={15} /> +{res.xp_gained} XP</p>}
      <div className="mt-6 flex justify-center gap-3">
        <button data-testid="quiz-retry" onClick={onRetry} className="rounded-full border border-zinc-700 px-4 py-2 text-[13px] text-zinc-200 hover:bg-zinc-800">Retake</button>
        <button data-testid="quiz-back-dashboard" onClick={onBack} className="rounded-full bg-blue-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-blue-500">Back to dashboard</button>
      </div>
    </div>
  );
}

export function Quiz({ mod, onDone, onBack }) {
  const [att, setAtt] = useState(null);
  const [idx, setIdx] = useState(0);
  const [fb, setFb] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(apiErr(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const start = () => run(async () => {
    const r = await academy.post(`/modules/${mod.id}/quiz/start`);
    setAtt(r.data); setIdx(0); setFb(null); setRes(null);
  });
  const answer = (i) => run(async () => {
    const r = await academy.post(`/quiz/${att.attempt_id}/answer`, { question_id: att.questions[idx].id, option: i });
    setFb({ ...r.data, chosen: i });
  });
  const next = () => run(async () => {
    if (idx + 1 < att.questions.length) { setIdx(idx + 1); setFb(null); return; }
    const r = await academy.post(`/quiz/${att.attempt_id}/finish`);
    setRes(r.data); setAtt(null);
    onDone();
  });
  if (res) return <Result res={res} onRetry={start} onBack={onBack} />;
  if (!att) return <Intro mod={mod} onStart={start} busy={busy} />;
  return <Question q={att.questions[idx]} idx={idx} total={att.questions.length} onAnswer={answer} feedback={fb} onNext={next} busy={busy} />;
}
